import Point = PIXI.Point;

interface IPathObject {
    points: number[];
    startDistance: number;
    arcLengths: number[];
    curveLength: number;
    endDistance: number;
    percentOfTrack?: number;
    startPercent?: number;
    endPercent?: number;
}

interface ICurveObject {
    arcLengths: number[];
    curveLength: number;
}

export interface IPathPoint {
    anchor: number[];
    leftDirection: number[];
    rightDirection: number[];
}

export default class BezierPath {
    public static getPathPoints(
        dataPoints: IPathPoint[],
        numNodes: number,
        debug?: PIXI.Graphics
    ): PIXI.Point[] {
        const paths: number[][] = this.convertPathToBeziers(dataPoints);
        const pathDataArr: IPathObject[] = this.createPathData(paths);
        const totalLineLength = pathDataArr[pathDataArr.length - 1].endDistance;

        // percentage of total track path for each curve
        // must be done after all paths are calculated
        pathDataArr.forEach((data: IPathObject) => {
            data.percentOfTrack = data.curveLength / totalLineLength;
            data.endPercent = data.endDistance / totalLineLength;
            data.startPercent = data.startDistance / totalLineLength;
        });

        const pathPoints = this.createPathPoints(numNodes, pathDataArr, totalLineLength);

        if (debug) {
            // draw for debugging
            this.drawLines(debug, paths);
        }
        return pathPoints;
    }

    private static createPathData(paths: number[][]): IPathObject[] {
        let startPoint = 0;
        return paths.reduce(
            (acc, path) => {
                const curveData: ICurveObject = this.getCurveData(path, startPoint);
                const pathObj: IPathObject = {
                    ...curveData,
                    endDistance: curveData.arcLengths[curveData.arcLengths.length - 1],
                    points: path,
                    startDistance: startPoint,
                };
                startPoint = pathObj.endDistance;
                acc.push(pathObj);
                return acc;
            },
            [] as IPathObject[]
        );
    }

    private static createPathPoints(
        numNodes: number,
        pathDataArr: IPathObject[],
        totalLineLength: number
    ): PIXI.Point[] {
        const pathPoints: PIXI.Point[] = [];
        const interval: number = 1 / numNodes;
        let curDist: number = 0;
        while (curDist <= 1) {
            const distPoint = BezierPath.getEvenTrackPoint(curDist, pathDataArr, totalLineLength);
            pathPoints.push(distPoint);
            curDist += interval;
        }

        return pathPoints;
    }

    private static drawLines(debug: PIXI.Graphics, paths: number[][]): void {
        debug.clear();
        debug.beginFill(0xffffff);

        for (const curPath of paths) {
            debug.beginFill(0xffffff);
            const path: number[] = curPath;
            const startPoint: Point = BezierPath.getCurvePoint(path, 0.01);
            debug.drawRect(startPoint.x, startPoint.y, 20, 20);
            debug.endFill();
            debug.beginFill(0x0000ff);
            const midPoint: Point = BezierPath.getCurvePoint(path, 0.5);
            debug.drawCircle(midPoint.x, midPoint.y, 10);
            debug.endFill();
            debug.beginFill(0xff0000);
            const endPoint: Point = BezierPath.getCurvePoint(path, 1);
            debug.drawRect(endPoint.x, endPoint.y, 30, 30);
            debug.endFill();
            debug.lineStyle(2);
            debug.moveTo(path[0], path[1]);

            const t: number = 0.01;
            for (let j: number = 0; j < 100; j++) {
                const linePoint: Point = BezierPath.getCurvePoint(path, t * j);
                debug.beginFill(0x0000ff);
                debug.drawCircle(linePoint.x, linePoint.y, 2);
                debug.endFill();
            }
        }
    }

    private static getCurveData(curve: number[], curveStartLength: number): ICurveObject {
        let totalLength: number = 0;
        const startPoint: Point = BezierPath.getCurvePoint(curve, 0);
        let lastX: number = startPoint.x;
        let lastY: number = startPoint.y;
        const t: number = 0.01;
        const arcLengths: number[] = [];

        for (let i: number = 0; i <= 100; i++) {
            const nextPt: Point = BezierPath.getCurvePoint(curve, i * t);
            totalLength += calcDist(nextPt.x, nextPt.y, lastX, lastY);
            arcLengths.push(curveStartLength + totalLength);
            lastX = nextPt.x;
            lastY = nextPt.y;
        }

        return { arcLengths, curveLength: totalLength };
    }

    private static getDistFromPercent(p: number, total: number): number {
        return total * p;
    }

    private static getCurvePoint(curve: number[], percent: number): Point {
        const a: Point = new Point(curve[0], curve[1]);
        const b: Point = new Point(curve[2], curve[3]);
        const c: Point = new Point(curve[4], curve[5]);
        const d: Point = new Point(curve[6], curve[7]);
        const pX: number =
            a.x * BezierPath.b1(percent) +
            b.x * BezierPath.b2(percent) +
            c.x * BezierPath.b3(percent) +
            d.x * BezierPath.b4(percent);
        const pY: number =
            a.y * BezierPath.b1(percent) +
            b.y * BezierPath.b2(percent) +
            c.y * BezierPath.b3(percent) +
            d.y * BezierPath.b4(percent);

        return new Point(pX, pY);
    }

    private static getEvenTrackPoint(p: number, pathDataArr: IPathObject[], total: number): Point {
        const dist = BezierPath.getDistFromPercent(p, total);
        let i: number = 0;
        while (pathDataArr[i].endDistance < dist) {
            i++;
        }
        const curPath: IPathObject = pathDataArr[i];
        return BezierPath.getEvenCurvePoint(curPath, dist, pathDataArr);
    }

    private static getEvenCurvePoint(
        curveData: IPathObject,
        dist: number,
        pathDataArr: IPathObject[]
    ): Point {
        const i = curveData.arcLengths.findIndex(len => len >= dist) - 1;
        const nextSeg: number =
            curveData.arcLengths.length > i
                ? curveData.arcLengths[i]
                : pathDataArr[pathDataArr.indexOf(curveData) + 1].startDistance;
        const lastLength: number =
            i === 0
                ? pathDataArr[pathDataArr.indexOf(curveData) - 1].endDistance
                : curveData.arcLengths[i - 1];
        let t: number = 0;
        if (lastLength === dist) {
            t = i / 100;
        } else {
            if (nextSeg === lastLength) {
                t = 0;
            } else {
                t = (i + (dist - lastLength) / (nextSeg - lastLength)) / 100;
            }
        }
        return BezierPath.getCurvePoint(curveData.points, t);
    }

    private static convertPathToBeziers(paths: IPathPoint[]): number[][] {
        return paths.reduce((acc, path, i, src) => {
            if (i === 0) {
                return acc;
            }
            const firstAnchor: number[] = src[i - 1].anchor;
            // firstAnchor[1] = firstAnchor[1] * -1; // maybe do this if value is less than 1 with illustrator weirdness...or math.abs
            const firstControl: number[] = i === 1 ? src[i - 1].anchor : src[i - 1].rightDirection; // had to reverse direction with removing negatives values from json
            const secondAnchor: number[] = path.anchor;
            const secondControl: number[] = i === src.length - 1 ? path.anchor : path.leftDirection;
            const bezier = [...firstAnchor, ...firstControl, ...secondControl, ...secondAnchor];
            acc.push(bezier);
            return acc;
        }, []);
    }

    private static b1(t: number): number {
        return (1 - t) * (1 - t) * (1 - t);
    }

    private static b2(t: number): number {
        return 3 * (1 - t) * (1 - t) * t;
    }

    private static b3(t: number): number {
        return 3 * (1 - t) * (t * t);
    }

    private static b4(t: number): number {
        return t * t * t;
    }

    /* private static getTrackPoint (dist:number, pathDataArr:IPathObject[]):Point {
        // const distPercent:number = MapPath.getTrackPercent(dist, totalLength);

        let i:number = 0;
        while (pathDataArr[i].endPercent < dist) {
            i++;
        }

        const curPath:IPathObject = pathDataArr[i];
        const percentOfCurPath:number = (dist - curPath.startPercent) / curPath.percentOfTrack;

        return MapPath.getCurvePoint(curPath.points, percentOfCurPath);
    } */

    /* private static getTrackPercent (dist:number, total:number):number {
        return dist / total
    } */
}

const calcDist = (x1: number, y1: number, x2: number, y2: number) => {
    const distX: number = x1 - x2;
    const distY: number = y1 - y2;

    return Math.sqrt(distX * distX + distY * distY);
};
